import React, { useState, useEffect } from 'react';
import axios from 'axios';

const api = () => axios.create({
  baseURL: '/api',
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

export default function PushNotificationToggle() {
  const [enabled, setEnabled] = useState(() => localStorage.getItem('push_enabled') === '1');
  const [loading, setLoading] = useState(false);
  const [supported, setSupported] = useState(true);

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setSupported(false);
    }
  }, []);

  const enable = async () => {
    setLoading(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        alert('Push notifications blocked. Please allow notifications in your browser settings.');
        setLoading(false);
        return;
      }
      // Get VAPID public key
      const { data } = await api().get('/push/vapid-public-key');
      const publicKey = data.publicKey;
      if (!publicKey) throw new Error('No VAPID key');

      // Register service worker
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });

      await api().post('/push/subscribe', { subscription });
      localStorage.setItem('push_enabled', '1');
      setEnabled(true);
    } catch (err) {
      console.error('Push enable error:', err);
      alert('Could not enable push notifications: ' + err.message);
    }
    setLoading(false);
  };

  const disable = async () => {
    setLoading(true);
    try {
      await api().delete('/push/unsubscribe');
      // Unsubscribe browser too
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) await sub.unsubscribe();
      } catch { /* ignore */ }
      localStorage.setItem('push_enabled', '0');
      setEnabled(false);
    } catch (err) {
      console.error('Push disable error:', err);
    }
    setLoading(false);
  };

  if (!supported) return null;

  return (
    <div className="flex items-start justify-between gap-4 p-4 border border-neutral-100 rounded-xl">
      <div>
        <p className="font-medium text-neutral-900 text-sm">Push notifications</p>
        <p className="text-xs text-neutral-500 mt-0.5">Get instant alerts for new hires, applications & shift reminders</p>
      </div>
      <button
        disabled={loading}
        onClick={enabled ? disable : enable}
        className={`shrink-0 w-12 h-6 rounded-full transition-colors relative disabled:opacity-50 ${enabled ? 'bg-brand-500' : 'bg-neutral-200'}`}
      >
        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${enabled ? 'left-6' : 'left-0.5'}`} />
      </button>
    </div>
  );
}
